import nxdStyles from '@n26/nxd/styles.css?inline'

import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
} from '@remix-run/react'

import { NXDProvider } from '@n26/nxd'

import type {
  MetaFunction,
  LinksFunction,
  LoaderFunctionArgs,
} from '@remix-run/node'
import { json } from '@remix-run/node'
import { useEffect } from 'react'
import styles from './styles.css?inline'
import fontStyles from './fonts.css?inline'
import baseStyles from './baseStyles.css?inline'

// There is an known issue with Remix where when the dev server
// rebuilds after a file change, it wipes out the font files and
// does not include them again in the dev build, rendering the pages font-less 🤡
// This is an known issue in Remix that was supposed to be closed,
// but looks like it's still lingering around: remix-run/remix#1153
//
// I'm including the N26 fonts in the App so we can work around this issue.
// It also makes the dev rebuilds a little faster (around ~400ms faster to rebuild).

import { askNotToTrackCookie } from './cookies.server'
import { getClientEnvironmentVariables } from '@infrastructure/env/factory.server'
import { getOneTrustScript } from '@core/getOneTrustScript'
import { config } from '@infrastructure/config'
import { GTMScript } from '@helpers/GTMScript'
import { ASK_NOT_TO_TRACK_HEADER } from '@constants/headers'
import { companyName } from '@constants/seo'
import { getOrCreateTracker } from '@helpers/core/snowplowClientInit'
import { useGetLang } from '@helpers/getLang'
import { SnowplowContext } from '@contexts/SnowplowContext'
import { useEventTracker } from '@hooks/useEventTracker'
import { EnvContext } from '@contexts/EnvContext'
import { ConfigContext } from '@contexts/ConfigContext'

export const links: LinksFunction = () => {
  return [
    {
      rel: 'preload',
      href: '/_build/fonts/GT-America-Standard-Regular.latin.woff2',
      as: 'font',
    },
    {
      rel: 'preload',
      href: '/_build/fonts/GT-America-Standard-Medium.latin.woff2',
      as: 'font',
    },
    {
      rel: 'preload',
      href: '/_build/fonts/GT-America-Extended-Medium.latin.woff2',
      as: 'font',
    },
    {
      rel: 'preconnect',
      href: 'https://images.ctfassets.net',
    },
    {
      rel: 'preconnect',
      href: 'https://cdn.cookielaw.org',
    },
  ]
}

export const meta: MetaFunction = () => {
  return [{ title: { companyName } }]
}

// need to load env config in a loader and get it below with useLoader
// to prevent client from attempting to execute server-side code
// see example here: https://github.com/remix-run/examples/blob/main/google-analytics/app/root.tsx
export async function loader({ context, request }: LoaderFunctionArgs) {
  const {
    oneTrustCookiePopup,
    gtm,
    snowplow,
    dataDogRUM,
    contentful,
    showEditModuleLink,
    showModuleErrors,
    showBelgium,
  } = config
  const { disableEncoding, collectorURL, defaultPropertyValue } = snowplow

  let clientEnv = getClientEnvironmentVariables()

  clientEnv = {
    ...clientEnv,
    // Make sure that client-side logging is disable in live builds
    ENABLE_CLIENT_SIDE_LOGGING: clientEnv.N26_ENV !== 'live',
  }

  let responseHeaders = {}

  const askNotToTrackHeader = request.headers.get(ASK_NOT_TO_TRACK_HEADER)
  const askNotToTrack = askNotToTrackHeader === 'true'

  const cookieHeader = request.headers.get('Cookie')
  const askNotToTrackRequestCookie =
    await askNotToTrackCookie.parse(cookieHeader)

  const shouldSetAskNotToTrackCookie =
    !askNotToTrackRequestCookie && askNotToTrack

  if (shouldSetAskNotToTrackCookie) {
    responseHeaders = {
      'Set-Cookie': await askNotToTrackCookie.serialize({
        askNotToTrack: true,
      }),
    }
  }

  return json(
    {
      oneTrustCookiePopup,
      askNotToTrack,
      ENV: clientEnv,
      dataDogRUM,
      gtm: {
        ...gtm,
        nonce: context?.nonce,
      },
      snowplow: {
        collectorURL,
        disableEncoding,
        defaultPropertyValue,
      },
      // @TODO: refactor to parse and allow only specific vars
      //        that are safe to be exposed in the client bundle
      //        similar to the ENV's getClientEnvironmentVariables
      publicConfig: {
        contentfulEnvironment: contentful?.environment,
        showEditModuleLink,
        showModuleErrors,
        showBelgium,
      },
    },
    {
      headers: {
        ...responseHeaders,
      },
    }
  )
}

export default function App() {
  const {
    oneTrustCookiePopup,
    ENV,
    gtm,
    askNotToTrack,
    snowplow,
    dataDogRUM,
    publicConfig,
  } = useLoaderData<typeof loader>()

  const shouldLoadOneTrustScript =
    oneTrustCookiePopup.isEnabled && !askNotToTrack

  const {
    nonce,
    tagManagerAuthToken,
    tagManagerEnvironment,
    tagManagerContainerId,
  } = gtm

  const { disableEncoding, collectorURL, defaultPropertyValue } = snowplow

  // initialize snowplow tracker
  getOrCreateTracker({
    collectorUrl: collectorURL,
    isSnowplowEncodingDisabled: disableEncoding,
  })

  const { pathname } = useLocation()

  const { track } = useEventTracker()

  // setting PAGE_VIEW event  on the top of the page
  useEffect(() => {
    track({ action: 'PAGE_VIEW', label: pathname })
    // we want to run the track function only when url changes
    // so pathname is the only dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  const datadogWindowVariabes = {
    ...dataDogRUM,
    clientToken: 'pub92adb0935c3adee642f4b3a581c9378d',
    applicationId: '1bc33d2e-bf7f-471a-a591-977c1be12a60',
    version: ENV.BUILD_COMMIT_ID,
    env: ENV.N26_ENV,
    service: 'nemo',
  }

  const lang = useGetLang()

  return (
    <html
      lang={lang}
      data-theme='light'
      className='font-standard scroll-smooth'
    >
      <head>
        <Meta />
        <Links />
        <style
          dangerouslySetInnerHTML={{
            __html: `${baseStyles} ${nxdStyles} ${fontStyles} ${styles}`,
          }}
        />

        {/* 
        Make sure that client-side code has access to some of our 
        environment variables. Only expose what is really public via the loader.
        * 
        * @see https://remix.run/docs/en/v1/guides/envvars#browser-environment-variables
       */}
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(ENV)}; 
            // setting datadog config variables on window so that they can be picked up in gtm tag
            window.datadogRum=${JSON.stringify(datadogWindowVariabes)}`,
          }}
        />
        {!askNotToTrack && (
          <GTMScript
            nonce={nonce}
            containerId={tagManagerContainerId}
            envName={tagManagerEnvironment}
            authToken={tagManagerAuthToken}
          />
        )}
        {shouldLoadOneTrustScript
          ? getOneTrustScript(oneTrustCookiePopup.domainScriptId)
          : null}
      </head>
      <body>
        <ConfigContext.Provider value={publicConfig}>
          <EnvContext.Provider value={ENV.N26_ENV}>
            <NXDProvider>
              <SnowplowContext.Provider
                value={{
                  defaultPropertyValue,
                }}
              >
                <div className='text-18 bg-neutral text-neutral font-sans'>
                  <Outlet />
                </div>
              </SnowplowContext.Provider>
            </NXDProvider>
          </EnvContext.Provider>
        </ConfigContext.Provider>

        <ScrollRestoration />

        <Scripts />
      </body>
    </html>
  )
}
